/**
 * Wishlist Hero — theme helpers (App Embed).
 *
 * - Registers custom add-to-wishlist nodes (event + SDK when available)
 * - Opens theme wishlist-drawer (native UI via WishListHero_SDK)
 * - Theme-owned Favoritos count badge
 *
 * @see https://d2uzwr8l81w3zz.cloudfront.net/Introduction.html
 * @see https://d2uzwr8l81w3zz.cloudfront.net/GetWishListItems.html
 * @see https://d2uzwr8l81w3zz.cloudfront.net/AddWishListItem.html
 * @see .cursor/references/wishlist-hero-notes.md
 */

const CUSTOM_BUTTON_SELECTOR = '.wishlist-hero-custom-button[data-wlh-id]';
const ADDED_CLASS = 'wishlisthero-product-in-wishlist';
const HOST_ADDED_CLASS = 'is-in-wishlist';
const COUNT_ATTR = 'data-wishlist-count';
const COUNT_STORAGE_KEY = 'mulaya:wishlist-count';
const BRIDGE_ID = 'wlh-open-bridge';
const FAVORITES_OPEN_SELECTOR =
  'a[href="#hero-wishlist"], a[href*="#hero-wishlist"], a.wishlist-hero-header-icon, a.header-actions__favorites, a.mobile-nav__favorites-link';
const VIEW_WISHLIST_TEXT = /view\s*(your\s*)?wishlist|ver\s*(tus?\s*)?favoritos|ver\s*la\s*wishlist/i;
const ADDED_TOAST_TEXT = /added\s+(successfully\s+)?to\s+wishlist|añadid[oa].*favoritos|agregad[oa].*favoritos/i;
const TOAST_HOST_ID = 'mulaya-wishlist-toast';

/** @type {boolean} */
let openingViaBridge = false;

/** @type {number | null} */
let toastHideTimer = null;

/** @type {boolean} */
let openingThemeDrawerFromWlh = false;

/** @type {number | null} */
let muiSwapTimer = null;

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function protocolRelativeUrl(value) {
  if (!value) return '';
  return value.replace(/^https?:/, '');
}

/**
 * @param {HTMLElement} button
 */
function normalizeButtonAttributes(button) {
  const image = button.getAttribute('data-wlh-image');
  if (image) {
    button.setAttribute('data-wlh-image', protocolRelativeUrl(image));
  }

  const variantId =
    button.getAttribute('data-wlh-variantid') || button.getAttribute('data-wlh-variantId');
  if (variantId) {
    button.setAttribute('data-wlh-variantId', variantId);
    button.setAttribute('data-wlh-variantid', variantId);
  }

  // WLH App Embed / docs suelen requerir esta clase para cablear el botón.
  button.classList.add('wishlisthero-floating');

  // No aplicar wishlisthero_buttonProdPageClasses aquí: son clases de botón ATC
  // (button, btn, …) y tapan el corazón de las cards con fondo opaco.
  button.classList.remove(
    'button',
    'button--full-width',
    'button-secondary',
    'btn',
    'btn--secondary-accent',
    'product__form--add-to-cart',
    'product-form__buttons'
  );
}

/**
 * true mientras el App Embed sigue inicializando.
 * @returns {boolean}
 */
function isWlhStillLoading() {
  const loading = window.__WishlistHero_Loading;
  return loading === true || loading === 1 || loading === 'true' || loading === '1';
}

/**
 * Espera a que WLH termine de cargar y entonces ejecuta el callback.
 * @param {() => void} callback
 * @param {{ maxTries?: number, intervalMs?: number }} [options]
 */
function whenWlhReady(callback, options = {}) {
  const maxTries = options.maxTries ?? 80;
  const intervalMs = options.intervalMs ?? 250;
  let tries = 0;

  const tick = () => {
    tries += 1;
    const readyEnough =
      !isWlhStillLoading() &&
      (Boolean(window.WishListHero_setting) || Boolean(window.WishListHero_SDK));

    if (readyEnough || tries >= maxTries) {
      debugLog('whenWlhReady', {
        tries,
        loading: window.__WishlistHero_Loading,
        hasSetting: Boolean(window.WishListHero_setting),
        hasSdk: Boolean(window.WishListHero_SDK),
        timedOut: tries >= maxTries && !readyEnough,
      });
      callback();
      return;
    }

    window.setTimeout(tick, intervalMs);
  };

  tick();
}

/**
 * Dispatch the event WLH listens for (document + window, bubbling).
 * @param {HTMLElement} button
 */
function dispatchAddToCustomElement(button) {
  const eventInit = {
    detail: button,
    bubbles: true,
    composed: true,
  };

  document.dispatchEvent(new CustomEvent('wishlist-hero-add-to-custom-element', eventInit));
  window.dispatchEvent(new CustomEvent('wishlist-hero-add-to-custom-element', eventInit));

  // Alias por si la build nueva usa otro nombre
  document.dispatchEvent(new CustomEvent('wishlist-hero-add-to-custom-button', eventInit));
  window.dispatchEvent(new CustomEvent('wishlist-hero-add-to-custom-button', eventInit));
}

/**
 * @param {HTMLElement} button
 */
function isButtonWired(button) {
  return Boolean(button.querySelector('button, a, svg, .wishlist-hero-custom-icon'));
}

/**
 * @param {HTMLElement} button
 * @returns {boolean}
 */
function initViaSdk(button) {
  const sdk = window.WishListHero_SDK;
  if (!sdk || typeof sdk.InitializeAddToWishListButton !== 'function') {
    return false;
  }

  const variantId = button.getAttribute('data-wlh-variantId') || button.getAttribute('data-wlh-variantid');
  const uid = `wlh-btn-${button.getAttribute('data-wlh-id')}-${variantId || '0'}`;
  button.classList.add(uid);

  try {
    sdk.InitializeAddToWishListButton({
      ButtonClassElement: `.${uid}`,
      ProductId: Number(button.getAttribute('data-wlh-id')),
      ProductLink: button.getAttribute('data-wlh-link') || '',
      ProductVariantId: Number(variantId),
      ProductPrice: Number(button.getAttribute('data-wlh-price') || 0),
      ProductTitle: button.getAttribute('data-wlh-name') || '',
      ProductImage: protocolRelativeUrl(button.getAttribute('data-wlh-image') || ''),
      ButtonMode: button.getAttribute('data-wlh-mode') || 'icon_only',
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {Element} button
 */
function initCustomButton(button) {
  if (!(button instanceof HTMLElement)) return;
  if (!button.hasAttribute('data-wlh-id')) return;

  if (isButtonWired(button)) {
    syncHostAddedState(button);
    return;
  }

  normalizeButtonAttributes(button);

  dispatchAddToCustomElement(button);

  initViaSdk(button);
  syncHostAddedState(button);
}

/**
 * @param {ParentNode} [root]
 */
function initCustomButtonsIn(root = document) {
  for (const button of root.querySelectorAll(CUSTOM_BUTTON_SELECTOR)) {
    initCustomButton(button);
  }
}

/**
 * @param {Element} node
 */
function syncHostAddedState(node) {
  const host = node.closest?.('.wishlist-hero-product-control');
  if (!host) return;

  const isAdded = Boolean(
    host.querySelector(`.${ADDED_CLASS}`) ||
      host.classList.contains(ADDED_CLASS) ||
      node.classList?.contains(ADDED_CLASS)
  );

  host.classList.toggle(HOST_ADDED_CLASS, isAdded);
  host.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
}

function syncAllHostAddedStates() {
  for (const host of document.querySelectorAll('.wishlist-hero-product-control')) {
    const button = host.querySelector(CUSTOM_BUTTON_SELECTOR) || host;
    syncHostAddedState(button);
  }
}

function stripInjectedCountFromLabels() {
  for (const label of document.querySelectorAll('.header-actions__favorites-label')) {
    if (!(label instanceof HTMLElement)) continue;
    const cleaned = label.textContent.replace(/\s*[\[(]\d+[\])]\s*/g, '').trim();
    if (cleaned) label.textContent = cleaned;
  }

  for (const link of document.querySelectorAll('a.header-actions__favorites, a.wishlist-hero-header-icon')) {
    if (!(link instanceof HTMLElement)) continue;
    if (link.id === BRIDGE_ID) continue;

    for (const node of [...link.childNodes]) {
      if (node.nodeType !== Node.TEXT_NODE) continue;
      const text = node.textContent || '';
      if (/^\s*[\[(]\d+[\])]\s*$/.test(text) || /^\s+$/.test(text)) {
        node.remove();
      }
    }

    for (const el of link.querySelectorAll(
      '[class*="count"]:not([data-wishlist-count]), .wlh-count, .wishlist-hero-count'
    )) {
      if (el.hasAttribute(COUNT_ATTR)) continue;
      el.remove();
    }
  }
}

/**
 * @param {number} count
 */
function paintHeaderCount(count) {
  const safeCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;

  try {
    sessionStorage.setItem(COUNT_STORAGE_KEY, String(safeCount));
  } catch {
    // ignore
  }

  stripInjectedCountFromLabels();

  for (const el of document.querySelectorAll(`[${COUNT_ATTR}]`)) {
    if (!(el instanceof HTMLElement)) continue;

    if (safeCount > 0) {
      const padded = safeCount < 10 ? `0${safeCount}` : String(safeCount);
      el.textContent = `[${padded}]`;
      el.hidden = false;
      el.removeAttribute('hidden');
    } else {
      el.textContent = '';
      el.hidden = true;
      el.setAttribute('hidden', '');
    }
  }

  for (const link of document.querySelectorAll('a.wishlist-hero-header-icon, a.header-actions__favorites')) {
    if (!(link instanceof HTMLElement) || link.id === BRIDGE_ID) continue;
    const label = link.getAttribute('aria-label') || '';
    const baseLabel = label.replace(/\s*[\[(]\d+[\])]\s*$/, '').trim() || label;
    if (safeCount > 0) {
      const padded = safeCount < 10 ? `0${safeCount}` : String(safeCount);
      link.setAttribute('aria-label', `${baseLabel} [${padded}]`);
    } else if (baseLabel) {
      link.setAttribute('aria-label', baseLabel);
    }
  }
}

/**
 * @returns {number | null}
 */
function readStoredCount() {
  try {
    const raw = sessionStorage.getItem(COUNT_STORAGE_KEY);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * @returns {Promise<number | null>}
 */
/**
 * @param {unknown} raw
 * @returns {unknown[] | null}
 */
function normalizeWishlistItemsPayload(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const record = /** @type {Record<string, unknown>} */ (raw);
    if (record.ok === false) return null;
    for (const key of ['items', 'Items', 'wishlistItems', 'WishlistItems', 'data']) {
      if (Array.isArray(record[key])) return /** @type {unknown[]} */ (record[key]);
    }
  }
  return null;
}

async function readCountFromSdk({ preferServer = false } = {}) {
  const sdk = window.WishListHero_SDK;
  if (!sdk) return null;

  try {
    let cachedCount = null;

    if (typeof sdk.GetWishListItemsCount === 'function') {
      const count = sdk.GetWishListItemsCount();
      if (typeof count === 'number') cachedCount = count;
      else if (count && typeof count.then === 'function') {
        const resolved = await count;
        if (typeof resolved === 'number') cachedCount = resolved;
      }
    }

    if (cachedCount == null && typeof sdk.GetWishListItems === 'function') {
      const items = normalizeWishlistItemsPayload(await sdk.GetWishListItems());
      if (items) cachedCount = items.length;
    }

    // Local cache is authoritative when it already has items.
    if (typeof cachedCount === 'number' && cachedCount > 0 && !preferServer) {
      return cachedCount;
    }

    if (preferServer && typeof sdk.GetWishListItemsFromServer === 'function') {
      const items = normalizeWishlistItemsPayload(await sdk.GetWishListItemsFromServer());
      if (items?.length) return items.length;
      // Empty/failed server must not wipe a non-zero local count.
      if (typeof cachedCount === 'number' && cachedCount > 0) return cachedCount;
      if (items) return 0;
    }

    if (typeof cachedCount === 'number') return cachedCount;
  } catch {
    return null;
  }

  return null;
}

/**
 * @returns {number}
 */
function countAddedButtonsOnPage() {
  const ids = new Set();

  for (const el of document.querySelectorAll(`.${ADDED_CLASS}[data-wlh-id], .${ADDED_CLASS}`)) {
    const host = el.closest('[data-wlh-id]') || el;
    const id = host.getAttribute?.('data-wlh-id');
    if (id) ids.add(id);
  }

  return ids.size;
}

/**
 * @param {number} delta
 */
function bumpHeaderCount(delta) {
  const current = readStoredCount();
  const base = typeof current === 'number' ? current : countAddedButtonsOnPage();
  paintHeaderCount(Math.max(0, base + delta));
}

/**
 * @param {{ preferServer?: boolean }} [options]
 */
async function refreshHeaderCount(options = {}) {
  const fromSdk = await readCountFromSdk({ preferServer: options.preferServer === true });
  if (typeof fromSdk === 'number') {
    paintHeaderCount(fromSdk);
    return;
  }

  // Without SDK, sessionStorage must not block optimistic/DOM updates forever.
  const onPage = countAddedButtonsOnPage();
  const stored = readStoredCount();

  if (typeof stored === 'number' && onPage > stored) {
    paintHeaderCount(onPage);
    return;
  }

  if (typeof stored === 'number') {
    paintHeaderCount(stored);
    return;
  }

  paintHeaderCount(onPage);
}

/**
 * Re-sync count after WLH finishes its async add/remove.
 */
function scheduleCountRefresh() {
  refreshHeaderCount({ preferServer: true });
  window.setTimeout(() => refreshHeaderCount({ preferServer: true }), 350);
  window.setTimeout(() => refreshHeaderCount({ preferServer: true }), 1000);
  window.setTimeout(() => refreshHeaderCount({ preferServer: true }), 2000);
}

/**
 * After a wishlist toggle, bump immediately then confirm with the SDK.
 * @param {HTMLElement} hostOrButton
 * @param {boolean} wasAdded
 */
function syncCountAfterToggle(hostOrButton, wasAdded) {
  const host =
    hostOrButton.closest?.('.wishlist-hero-product-control') ||
    hostOrButton.closest?.('#wishlisthero-product-page-button-container') ||
    hostOrButton;

  const isAdded = Boolean(
    host?.classList?.contains(HOST_ADDED_CLASS) ||
      host?.classList?.contains(ADDED_CLASS) ||
      host?.querySelector?.(`.${ADDED_CLASS}`) ||
      hostOrButton.classList?.contains(ADDED_CLASS)
  );

  if (!wasAdded && isAdded) {
    bumpHeaderCount(1);
    showMulayaWishlistToast();
  } else if (wasAdded && !isAdded) {
    bumpHeaderCount(-1);
  }

  scheduleCountRefresh();
}

/**
 * @param {Element} root
 * @returns {boolean}
 */
function isWishlistMuiRoot(root) {
  if (root.id === 'hero-wishlist') return true;

  return Boolean(
    root.querySelector?.(
      '.wishlist-hero-header, .wishlisthero-customer-view-listing, .wishlist-hero-list-card, #hero-wishlist'
    )
  );
}

/**
 * Tear down WLH MUI modal/backdrop so the page is not left inert after “View wishlist”.
 */
function dismissWlhMuiOverlays() {
  const sdk = window.WishListHero_SDK;
  if (sdk && typeof sdk.CloseWishList === 'function') {
    try {
      sdk.CloseWishList();
    } catch {
      // ignore
    }
  }

  for (const modal of document.querySelectorAll('.MuiModal-root, .MuiDialog-root, #hero-wishlist')) {
    if (!(modal instanceof HTMLElement)) continue;
    if (!isWishlistMuiRoot(modal) && modal.id !== 'hero-wishlist') continue;

    modal.setAttribute('aria-hidden', 'true');
    modal.style.setProperty('display', 'none', 'important');
    modal.style.setProperty('visibility', 'hidden', 'important');
    modal.style.setProperty('pointer-events', 'none', 'important');

    for (const backdrop of modal.querySelectorAll('.MuiBackdrop-root')) {
      if (backdrop instanceof HTMLElement) {
        backdrop.style.setProperty('pointer-events', 'none', 'important');
        backdrop.style.setProperty('display', 'none', 'important');
      }
    }
  }

  if (location.hash === '#hero-wishlist' || location.hash.startsWith('#hero-wishlist')) {
    try {
      history.replaceState(null, '', `${location.pathname}${location.search}`);
    } catch {
      // ignore
    }
  }

  const themeDialog = document.querySelector('#wishlist-drawer-dialog');
  const themeOpen =
    themeDialog instanceof HTMLDialogElement
      ? themeDialog.open
      : themeDialog?.hasAttribute('open');

  if (!themeOpen) {
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
    if (document.body.style.position === 'fixed') {
      const scrollY = document.body.style.top;
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      document.body.style.removeProperty('left');
      document.body.style.removeProperty('right');
      if (scrollY) {
        const y = Math.abs(Number.parseInt(scrollY, 10) || 0);
        window.scrollTo(0, y);
      }
    }
  }

  for (const el of document.querySelectorAll('[aria-hidden="true"]')) {
    if (!(el instanceof HTMLElement)) continue;
    if (el.closest('.MuiModal-root, wishlist-drawer, dialog')) continue;
    if (el.getAttribute('data-mulaya-aria-hidden') === '1') {
      el.removeAttribute('aria-hidden');
      el.removeAttribute('data-mulaya-aria-hidden');
    }
  }
}

/**
 * Hide App Embed snackbars (English / under Inbox).
 */
function hideWlhToasts() {
  for (const el of document.querySelectorAll('.MuiSnackbar-root, [class*="notistack"]')) {
    if (!(el instanceof HTMLElement)) continue;
    const text = (el.textContent || '').trim();
    if (!ADDED_TOAST_TEXT.test(text) && !VIEW_WISHLIST_TEXT.test(text) && !/wishlist|favoritos/i.test(text)) {
      continue;
    }
    el.style.setProperty('display', 'none', 'important');
    el.style.setProperty('visibility', 'hidden', 'important');
    el.style.setProperty('pointer-events', 'none', 'important');
    el.setAttribute('aria-hidden', 'true');
  }
}

/**
 * @returns {{ added: string, view: string }}
 */
function getToastLabels() {
  const drawer = document.getElementById('wishlist-drawer');
  return {
    added: drawer?.getAttribute('data-label-toast-added') || 'Añadido a favoritos',
    view: drawer?.getAttribute('data-label-toast-view') || 'Ver favoritos',
  };
}

/**
 * Theme toast in store locale — replaces the App Embed EN notification.
 */
function showMulayaWishlistToast() {
  hideWlhToasts();

  const labels = getToastLabels();
  let toast = document.getElementById(TOAST_HOST_ID);

  if (!(toast instanceof HTMLElement)) {
    toast = document.createElement('div');
    toast.id = TOAST_HOST_ID;
    toast.className = 'mulaya-wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    const message = document.createElement('span');
    message.className = 'mulaya-wishlist-toast__message';
    message.dataset.mulayaToastMessage = '1';

    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'mulaya-wishlist-toast__action';
    action.dataset.mulayaToastAction = '1';
    action.addEventListener('click', () => {
      hideMulayaWishlistToast();
      dismissWlhMuiOverlays();
      openWishlistModal();
    });

    toast.append(message, action);
    document.body.appendChild(toast);
  }

  const messageEl = toast.querySelector('[data-mulaya-toast-message]');
  const actionEl = toast.querySelector('[data-mulaya-toast-action]');
  if (messageEl) messageEl.textContent = labels.added;
  if (actionEl) actionEl.textContent = labels.view;

  toast.classList.add('is-visible');

  if (toastHideTimer != null) window.clearTimeout(toastHideTimer);
  toastHideTimer = window.setTimeout(() => {
    hideMulayaWishlistToast();
  }, 4500);
}

function hideMulayaWishlistToast() {
  const toast = document.getElementById(TOAST_HOST_ID);
  if (toast instanceof HTMLElement) {
    toast.classList.remove('is-visible');
  }
  if (toastHideTimer != null) {
    window.clearTimeout(toastHideTimer);
    toastHideTimer = null;
  }
}

/**
 * Abre el drawer nativo del tema (Wishlist Hero SDK). Ya no se usa el modal MUI.
 */
async function openWishlistModal() {
  openingThemeDrawerFromWlh = true;
  dismissWlhMuiOverlays();
  hideWlhToasts();
  hideMulayaWishlistToast();

  try {
    await customElements.whenDefined('wishlist-drawer');
  } catch (_error) {
    // Custom element registry unavailable — fall through.
  }

  const drawer = document.getElementById('wishlist-drawer');
  if (drawer && typeof /** @type {{ openDrawer?: () => void }} */ (drawer).openDrawer === 'function') {
    /** @type {{ openDrawer: () => void }} */ (drawer).openDrawer();
    debugLog('openWishlistModal → theme wishlist-drawer');
    window.setTimeout(() => {
      dismissWlhMuiOverlays();
      openingThemeDrawerFromWlh = false;
    }, 100);
    return;
  }

  openingThemeDrawerFromWlh = false;
  debugLog('openWishlistModal: wishlist-drawer missing');
}

/**
 * @returns {boolean}
 */
function revealWlhModal() {
  const selectors = [
    '#hero-wishlist',
    '[id*="hero-wishlist"]',
    '.wishlist-hero-modal',
    '.wishlist-hero-popup',
    '.wishlisthero-modal',
    '.wishlisthero-popup',
    '[class*="wishlist-hero"][class*="modal"]',
    '[class*="wishlist-hero"][class*="popup"]',
    '[class*="wishlisthero"][class*="modal"]',
    '[class*="wlh"][class*="modal"]',
  ];

  for (const selector of selectors) {
    for (const el of document.querySelectorAll(selector)) {
      if (!(el instanceof HTMLElement)) continue;
      if (el.id === BRIDGE_ID) continue;
      if (el.matches('a, script, link, style')) continue;

      el.hidden = false;
      el.removeAttribute('hidden');
      el.style.setProperty('display', 'block', 'important');
      el.style.setProperty('visibility', 'visible', 'important');
      el.style.setProperty('opacity', '1', 'important');
      el.style.setProperty('z-index', '2147483000', 'important');
      el.classList.add('open', 'show', 'active', 'is-open', 'visible');

      const dialog = el.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement && !dialog.open) {
        try {
          dialog.showModal();
        } catch {
          dialog.setAttribute('open', '');
        }
      }

      return true;
    }
  }

  return false;
}

/**
 * @param {Element} el
 * @returns {boolean}
 */
function isViewWishlistControl(el) {
  if (el.matches?.(FAVORITES_OPEN_SELECTOR)) return true;

  const href = el.getAttribute?.('href') || '';
  if (href === '#hero-wishlist' || href.includes('#hero-wishlist')) return true;

  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (VIEW_WISHLIST_TEXT.test(text) && el.closest('.MuiSnackbar-root, [class*="notistack"], .MuiAlert-root')) {
    return true;
  }

  return false;
}

/**
 * @param {MouseEvent} event
 */
function handleFavoritesClick(event) {
  if (openingViaBridge) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const control =
    target.closest(
      `${FAVORITES_OPEN_SELECTOR}, .MuiSnackbar-root a, .MuiSnackbar-root button, [class*="notistack"] a, [class*="notistack"] button`
    ) || null;

  if (!control || control.id === BRIDGE_ID) return;
  if (!isViewWishlistControl(control) && !control.matches?.(FAVORITES_OPEN_SELECTOR)) return;

  const href = control.getAttribute('href');
  if (
    href &&
    href !== '#hero-wishlist' &&
    !href.includes('#hero-wishlist') &&
    !VIEW_WISHLIST_TEXT.test((control.textContent || '').trim())
  ) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  if (typeof event.stopImmediatePropagation === 'function') {
    event.stopImmediatePropagation();
  }
  debugLog('click Favoritos / View wishlist → openWishlistModal', {
    href: control.getAttribute('href'),
    text: (control.textContent || '').trim(),
    hashBefore: location.hash,
    sdk: Boolean(window.WishListHero_SDK),
  });
  openWishlistModal();
}

function handleWishlistHashChange() {
  if (location.hash === '#hero-wishlist' || location.hash.startsWith('#hero-wishlist')) {
    debugLog('hashchange → openWishlistModal');
    openWishlistModal();
  }
}

/**
 * @param {HTMLElement} customButton
 * @returns {Promise<boolean>}
 */
async function addViaSdk(customButton) {
  const sdk = window.WishListHero_SDK;
  if (!sdk || typeof sdk.AddWishListItem !== 'function') {
    return false;
  }

  const wasAdded = customButton.classList.contains(ADDED_CLASS);

  try {
    let wishlistHash = '';
    if (typeof sdk.GetWishListHash === 'function') {
      wishlistHash = await sdk.GetWishListHash(true);
    }

    const variantId =
      customButton.getAttribute('data-wlh-variantId') || customButton.getAttribute('data-wlh-variantid');

    await sdk.AddWishListItem({
      ProductId: Number(customButton.getAttribute('data-wlh-id')),
      ProductTitle: customButton.getAttribute('data-wlh-name') || '',
      ProductPrice: Number(customButton.getAttribute('data-wlh-price') || 0),
      ProductLink: customButton.getAttribute('data-wlh-link') || '',
      ProductVariantId: Number(variantId),
      ProductImage: protocolRelativeUrl(customButton.getAttribute('data-wlh-image') || ''),
      WishlistHash: wishlistHash,
    });

    customButton.classList.add(ADDED_CLASS);
    syncHostAddedState(customButton);
    syncCountAfterToggle(customButton, wasAdded);
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {number} ms
 */
function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * @param {HTMLElement} host
 */
async function addFromProductControl(host) {
  const custom = host.querySelector(CUSTOM_BUTTON_SELECTOR);
  if (!(custom instanceof HTMLElement)) return;

  const wasAdded = Boolean(
    custom.classList.contains(ADDED_CLASS) || host.classList.contains(HOST_ADDED_CLASS)
  );

  let wired = custom.querySelector('button, a');
  if (wired instanceof HTMLElement) {
    wired.click();
    window.setTimeout(() => {
      syncHostAddedState(custom);
      syncCountAfterToggle(host, wasAdded);
    }, 400);
    return;
  }

  initCustomButton(custom);
  await wait(350);

  wired = custom.querySelector('button, a');
  if (wired instanceof HTMLElement) {
    wired.click();
    window.setTimeout(() => {
      syncHostAddedState(custom);
      syncCountAfterToggle(host, wasAdded);
    }, 400);
    return;
  }

  const added = await addViaSdk(custom);
  if (!added) {
    dispatchAddToCustomElement(custom);
    await wait(500);
    wired = custom.querySelector('button, a');
    if (wired instanceof HTMLElement) {
      wired.click();
      window.setTimeout(() => {
        syncHostAddedState(custom);
        syncCountAfterToggle(host, wasAdded);
      }, 400);
    }
  }
}

/**
 * @param {MouseEvent} event
 */
function handleProductControlClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const host = target.closest('.wishlist-hero-product-control');
  if (!host) return;

  // Si el clic ya es sobre el control inyectado por WLH, dejarlo.
  if (target.closest('.wishlist-hero-custom-button button, .wishlist-hero-custom-button a')) {
    const wasAdded = Boolean(
      host.classList.contains(HOST_ADDED_CLASS) || host.querySelector(`.${ADDED_CLASS}`)
    );
    window.setTimeout(() => {
      const custom = host.querySelector(CUSTOM_BUTTON_SELECTOR);
      if (custom) {
        syncHostAddedState(custom);
        syncCountAfterToggle(host, wasAdded);
      }
    }, 400);
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  debugLog('click product-control → addFromProductControl', {
    host: host.className,
    wired: Boolean(host.querySelector('.wishlist-hero-custom-button button, .wishlist-hero-custom-button a')),
  });
  addFromProductControl(host);
}

/**
 * PDP native WLH button — App Embed owns the click; theme only refreshes the header count.
 * @param {MouseEvent} event
 */
function handleNativePdpWishlistClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const nativeBtn = target.closest(
    '.product-wishlist-native > button, #wishlisthero-product-page-button-container button, [data-wlh-pdp-slot] button'
  );
  if (!(nativeBtn instanceof HTMLElement)) return;

  const container =
    nativeBtn.closest(
      '#wishlisthero-product-page-button-container, .product-wishlist-native, [data-wlh-pdp-slot]'
    ) || nativeBtn;

  const labelBefore = (nativeBtn.textContent || '').toLowerCase();
  const wasAdded = Boolean(
    container.classList.contains(ADDED_CLASS) ||
      container.querySelector(`.${ADDED_CLASS}`) ||
      nativeBtn.classList.contains(ADDED_CLASS) ||
      /elimin|remove|quitar/.test(labelBefore)
  );

  window.setTimeout(() => {
    const labelAfter = (nativeBtn.textContent || '').toLowerCase();
    const isAdded = Boolean(
      container.classList.contains(ADDED_CLASS) ||
        container.querySelector(`.${ADDED_CLASS}`) ||
        nativeBtn.classList.contains(ADDED_CLASS) ||
        /elimin|remove|quitar/.test(labelAfter)
    );

    if (!wasAdded && isAdded) {
      bumpHeaderCount(1);
      showMulayaWishlistToast();
    } else if (wasAdded && !isAdded) {
      bumpHeaderCount(-1);
    }

    scheduleCountRefresh();
  }, 500);
}

function onSdkReady() {
  placeNativePdpButton();
  initCustomButtonsIn(document);
  syncAllHostAddedStates();
  refreshHeaderCount();

  // Deep-link: abrir drawer nativo del tema
  if (location.hash === '#hero-wishlist') {
    openWishlistModal();
  }
}

/**
 * Sync contador + estado filled tras cambios del drawer nativo.
 * @param {Event} event
 */
function handleWishlistUpdated(event) {
  const detail =
    event instanceof CustomEvent && event.detail && typeof event.detail === 'object'
      ? /** @type {{ count?: number, items?: Array<{ ProductVariantId?: string|number }> }} */ (event.detail)
      : {};

  if (typeof detail.count === 'number') {
    paintHeaderCount(detail.count);
  } else {
    scheduleCountRefresh();
  }

  if (!Array.isArray(detail.items)) {
    syncAllHostAddedStates();
    return;
  }

  const variantIds = new Set(
    detail.items.map((item) => String(item.ProductVariantId ?? '')).filter(Boolean)
  );

  for (const node of document.querySelectorAll(CUSTOM_BUTTON_SELECTOR)) {
    if (!(node instanceof HTMLElement)) continue;

    const variantId =
      node.getAttribute('data-wlh-variantId') || node.getAttribute('data-wlh-variantid');
    if (!variantId) continue;

    if (variantIds.has(String(variantId))) {
      node.classList.add(ADDED_CLASS);
    } else {
      node.classList.remove(ADDED_CLASS);
    }

    syncHostAddedState(node);
  }
}

/** @type {number | null} */
let placeNativeRaf = null;

function schedulePlaceNativePdpButton() {
  if (placeNativeRaf != null) return;
  placeNativeRaf = window.requestAnimationFrame(() => {
    placeNativeRaf = null;
    placeNativePdpButton();
  });
}

/**
 * Coloca el botón nativo de PDP en el slot bajo el ATC.
 * Si el tema ya pinta `.product-wishlist-button`, no reemplaza el slot
 * (el control custom es la fuente de verdad en PDP).
 */
function placeNativePdpButton() {
  const slot = document.querySelector('[data-wlh-pdp-slot]');
  if (!(slot instanceof HTMLElement)) return;

  // Theme control already visible — keep native container hidden / out of the way
  if (slot.querySelector('.product-wishlist-button')) {
    for (const el of slot.querySelectorAll('.product-wishlist-native, #wishlisthero-product-page-button-container')) {
      if (el instanceof HTMLElement) el.hidden = true;
    }
    return;
  }

  const natives = [
    ...document.querySelectorAll(
      '#wishlisthero-product-page-button-container, .wishlisthero-product-page-button-container'
    ),
  ];

  if (natives.length === 0) return;

  const withButton =
    natives.find((el) => el.querySelector('button')) || natives[0];

  for (const el of natives) {
    if (el === withButton) continue;
    if (!el.querySelector('button')) {
      el.remove();
    }
  }

  if (!(withButton instanceof HTMLElement)) return;

  withButton.classList.add('product-wishlist-native');
  withButton.id = 'wishlisthero-product-page-button-container';
  withButton.hidden = false;

  if (!slot.contains(withButton)) {
    slot.replaceChildren(withButton);
    debugLog('native PDP button moved into slot');
  }
}

function scheduleInitRetries() {
  const delays = [200, 600, 1200, 2500, 5000, 8000];

  for (const delay of delays) {
    window.setTimeout(() => {
      placeNativePdpButton();
      initCustomButtonsIn(document);
      syncAllHostAddedStates();
      refreshHeaderCount();
      stripInjectedCountFromLabels();
      debugLog(`retry init @${delay}ms`, {
        wired: [...document.querySelectorAll(CUSTOM_BUTTON_SELECTOR)].filter((el) =>
          isButtonWired(/** @type {HTMLElement} */ (el))
        ).length,
        total: document.querySelectorAll(CUSTOM_BUTTON_SELECTOR).length,
        sdk: Boolean(window.WishListHero_SDK),
        nativePdp: Boolean(
          document.querySelector('.product-wishlist-native > button, #wishlisthero-product-page-button-container button')
        ),
      });
    }, delay);
  }
}

/**
 * Espera a que carguen default.js / bundle2.js de WLH y entonces re-init.
 */
function waitForWlhScriptsThenInit() {
  const isWlhAsset = (src) =>
    typeof src === 'string' &&
    /wishlist-hero|wishlisthero/i.test(src) &&
    /(default|bundle2)\.js/i.test(src);

  const pending = [...document.scripts].filter(
    (script) => isWlhAsset(script.src) && !script.dataset.mulayaWlhHooked
  );

  for (const script of pending) {
    script.dataset.mulayaWlhHooked = '1';
    script.addEventListener(
      'load',
      () => {
        debugLog('WLH script load', script.src);
        initCustomButtonsIn(document);
        if (isDebugEnabled()) printDiagnosis();
      },
      { once: true }
    );
  }

  window.setTimeout(() => initCustomButtonsIn(document), 100);
}

/**
 * @param {Element} node
 */
function handleWlhOverlayNode(node) {
  if (node.matches?.('.MuiSnackbar-root, [class*="notistack"]') || node.querySelector?.('.MuiSnackbar-root')) {
    hideWlhToasts();
    const text = (node.textContent || '').trim();
    if (ADDED_TOAST_TEXT.test(text) || VIEW_WISHLIST_TEXT.test(text)) {
      showMulayaWishlistToast();
    }
  }

  const modal =
    node.matches?.('.MuiModal-root, .MuiDialog-root')
      ? node
      : node.querySelector?.('.MuiModal-root, .MuiDialog-root');

  if (!(modal instanceof HTMLElement) || !isWishlistMuiRoot(modal)) return;
  if (openingThemeDrawerFromWlh) {
    dismissWlhMuiOverlays();
    return;
  }

  // Toast/header “View wishlist” opened MUI — swap to theme drawer (debounced).
  if (muiSwapTimer != null) window.clearTimeout(muiSwapTimer);
  muiSwapTimer = window.setTimeout(() => {
    muiSwapTimer = null;
    debugLog('WLH MUI modal detected → dismiss + theme drawer');
    dismissWlhMuiOverlays();
    openWishlistModal();
  }, 0);
}

function observeDom() {
  const observer = new MutationObserver((mutations) => {
    let shouldSyncHosts = false;
    let shouldRefreshCount = false;

    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const el = mutation.target;
        if (el instanceof Element) {
          if (
            el.classList.contains(ADDED_CLASS) ||
            el.classList.contains('wishlist-hero-custom-button') ||
            el.closest?.(CUSTOM_BUTTON_SELECTOR)
          ) {
            syncHostAddedState(el);
            shouldSyncHosts = true;
            shouldRefreshCount = true;
          }
        }
      }

      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.TEXT_NODE && /\(\d+\)/.test(node.textContent || '')) {
          stripInjectedCountFromLabels();
          continue;
        }

        if (!(node instanceof Element)) continue;

        handleWlhOverlayNode(node);

        if (node.matches?.(CUSTOM_BUTTON_SELECTOR)) {
          initCustomButton(node);
        }

        if (
          node.matches?.('#wishlisthero-product-page-button-container, .wishlisthero-product-page-button-container') ||
          node.querySelector?.(
            '#wishlisthero-product-page-button-container, .wishlisthero-product-page-button-container'
          )
        ) {
          schedulePlaceNativePdpButton();
        }

        if (node.querySelectorAll) {
          initCustomButtonsIn(node);
        }

        if (node.classList?.contains(ADDED_CLASS) || node.querySelector?.(`.${ADDED_CLASS}`)) {
          shouldSyncHosts = true;
          shouldRefreshCount = true;
        }
      }
    }

    if (shouldSyncHosts) syncAllHostAddedStates();
    if (shouldRefreshCount) scheduleCountRefresh();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
    characterData: true,
  });
}

function ensureBridge() {
  if (document.getElementById(BRIDGE_ID)) return;

  const bridge = document.createElement('a');
  bridge.id = BRIDGE_ID;
  bridge.href = '#hero-wishlist';
  bridge.className = 'wishlist-hero-header-icon wishlist-hero-header-show-count-only visually-hidden';
  bridge.setAttribute('aria-hidden', 'true');
  bridge.tabIndex = -1;
  bridge.textContent = 'Wishlist';
  document.body.appendChild(bridge);
}

/**
 * @returns {boolean}
 */
function isDebugEnabled() {
  try {
    if (new URLSearchParams(location.search).get('wlh_debug') === '1') return true;
    if (localStorage.getItem('mulaya:wlh_debug') === '1') return true;
  } catch {
    // ignore
  }
  return false;
}

/**
 * Diagnóstico para pegar en consola o con ?wlh_debug=1
 * @returns {Record<string, unknown>}
 */
function diagnoseWishlistHero() {
  const sdk = window.WishListHero_SDK;
  const scripts = [...document.scripts]
    .map((s) => s.src)
    .filter((src) => /wishlist|wlh|revamp/i.test(src));

  const globals = Object.keys(window)
    .filter((key) => /wish|wlh|revamp/i.test(key))
    .slice(0, 40);

  const buttons = [...document.querySelectorAll(CUSTOM_BUTTON_SELECTOR)].map((el) => {
    const host = el.closest('.wishlist-hero-product-control');
    return {
      id: el.getAttribute('data-wlh-id'),
      variantId: el.getAttribute('data-wlh-variantId') || el.getAttribute('data-wlh-variantid'),
      mode: el.getAttribute('data-wlh-mode'),
      view: el.getAttribute('data-wlh-view'),
      hasFloating: el.classList.contains('wishlisthero-floating'),
      classes: el.className,
      wired: isButtonWired(/** @type {HTMLElement} */ (el)),
      childHTML: el.innerHTML.slice(0, 180),
      inHost: Boolean(host),
      hostClasses: host?.className || null,
      isAdded: el.classList.contains(ADDED_CLASS) || Boolean(host?.classList.contains(HOST_ADDED_CLASS)),
    };
  });

  const favoritesLinks = [...document.querySelectorAll(FAVORITES_OPEN_SELECTOR)].map((a) => ({
    id: a.id || null,
    href: a.getAttribute('href'),
    classes: a.className,
    visible: a instanceof HTMLElement ? a.offsetParent !== null || a.getClientRects().length > 0 : false,
  }));

  const modalCandidates = [...document.querySelectorAll(
    '[id*="hero-wishlist"], [id*="wishlist"], [class*="wishlist-hero"], [class*="wishlisthero"]'
  )]
    .slice(0, 30)
    .map((el) => ({
      tag: el.tagName,
      id: el.id,
      classes: el.className?.toString?.().slice(0, 120),
      display: el instanceof HTMLElement ? getComputedStyle(el).display : null,
    }));

  const nativePdp = document.querySelector(
    '.product-wishlist-native > button, #wishlisthero-product-page-button-container button'
  );

  const report = {
    ok: {
      themeInitLoaded: true,
      sdkPresent: Boolean(sdk),
      sdkOpenWishList: Boolean(sdk && typeof sdk.OpenWishList === 'function'),
      sdkAddWishListItem: Boolean(sdk && typeof sdk.AddWishListItem === 'function'),
      sdkInitializeButton: Boolean(sdk && typeof sdk.InitializeAddToWishListButton === 'function'),
      bridgePresent: Boolean(document.getElementById(BRIDGE_ID)),
      customButtonsFound: buttons.length > 0,
      anyButtonWired: buttons.some((b) => b.wired),
      allHaveFloating: buttons.length > 0 && buttons.every((b) => b.hasFloating),
      favoritesLinksFound: favoritesLinks.length > 0,
      nativePdpButton: Boolean(nativePdp),
      nativePdpInSlot: Boolean(
        document.querySelector('[data-wlh-pdp-slot] .wishlisthero-product-page-button-container button')
      ),
    },
    page: {
      path: location.pathname,
      hash: location.hash,
      template: document.querySelector('main')?.getAttribute('data-template') || null,
    },
    globals,
    sdkKeys: sdk ? Object.keys(sdk).slice(0, 40) : [],
    wlhScripts: scripts,
    buttons,
    favoritesLinks,
    modalCandidates,
    verdicts: /** @type {string[]} */ ([]),
  };

  if (!report.ok.sdkPresent && scripts.length === 0) {
    report.verdicts.push(
      'FALLO: No hay SDK ni scripts de Wishlist Hero. Revisa App Embed activo en el tema publicado/preview.'
    );
  } else if (!report.ok.sdkPresent) {
    report.verdicts.push(
      'AVISO: Hay scripts WLH pero window.WishListHero_SDK aún no existe (carga async o plan/API).'
    );
  }

  if (!report.ok.nativePdpButton && /\/products\//.test(location.pathname)) {
    report.verdicts.push(
      'PDP: sin botón nativo WLH (ok si hay .product-wishlist-button del tema).'
    );
  }

  if (!report.ok.customButtonsFound) {
    report.verdicts.push(
      'FALLO: No hay .wishlist-hero-custom-button en el DOM (snippet no se renderiza en esta página).'
    );
  } else if (!report.ok.anyButtonWired) {
    report.verdicts.push(
      'FALLO: Hay botones custom pero WLH no los ha cableado (sin <button> interno). El evento add-to-custom-element no está haciendo efecto.'
    );
    if (isWlhStillLoading()) {
      report.verdicts.push(
        'AVISO: __WishlistHero_Loading sigue activo — el App Embed aún no ha terminado de inicializar.'
      );
    } else {
      report.verdicts.push(
        'Siguiente paso: await window.__mulayaWlhInspectScripts() y revisa customEvents/listeners. También window.__mulayaWlhReinit().'
      );
    }
  }

  if (buttons.length && !report.ok.allHaveFloating) {
    report.verdicts.push(
      'AVISO: Algún botón no tiene clase wishlisthero-floating (WLH suele requerirla).'
    );
  }

  if (!report.ok.sdkOpenWishList) {
    report.verdicts.push(
      'AVISO: OpenWishList no disponible (JS API suele requerir Silver+). El open depende de hash/bridge/DOM.'
    );
  }

  if (location.hash === '#hero-wishlist') {
    const visibleModal = modalCandidates.some((m) => m.display && m.display !== 'none');
    if (!visibleModal) {
      report.verdicts.push(
        'FALLO: Hash #hero-wishlist presente pero no hay modal WLH visible (display:none / no montado).'
      );
    }
  }

  if (report.verdicts.length === 0) {
    report.verdicts.push('SDK/botones parecen presentes. Si el clic falla, mira el log [WLH] click en consola.');
  }

  return report;
}

/**
 * @param {string} label
 * @param {unknown} [extra]
 */
function debugLog(label, extra) {
  if (!isDebugEnabled()) return;
  if (extra !== undefined) {
    console.info(`[WLH] ${label}`, extra);
  } else {
    console.info(`[WLH] ${label}`);
  }
}

function printDiagnosis() {
  const report = diagnoseWishlistHero();
  console.group('%c[WLH diagnóstico Mulaya]', 'font-weight:bold;color:#0b5');
  console.table(report.ok);
  console.log('Página', report.page);
  console.log('Globals WLH', report.globals);
  console.log('__WishlistHero_Loading', window.__WishlistHero_Loading);
  console.log('WishListHero_setting', window.WishListHero_setting);
  console.log('wishlisthero_buttonProdPageClasses', window.wishlisthero_buttonProdPageClasses);
  console.log('SDK keys', report.sdkKeys);
  console.log('Scripts WLH', report.wlhScripts);
  console.log('Botones custom', report.buttons);
  console.log('Links Favoritos', report.favoritesLinks);
  console.log('Candidatos modal', report.modalCandidates);
  console.log('Veredictos:');
  for (const v of report.verdicts) {
    console.warn(v);
  }
  console.info('Repite con: window.__mulayaWlhDebug()');
  console.info('Forzar open: window.__mulayaWlhOpen()');
  console.info('Forzar add primer botón PDP: window.__mulayaWlhAddFirst()');
  console.info('Inspeccionar scripts WLH: await window.__mulayaWlhInspectScripts()');
  console.groupEnd();
  return report;
}

/**
 * Descarga default.js/bundle2.js y busca el nombre real del evento de init.
 * @returns {Promise<object[]>}
 */
async function inspectWlhScripts() {
  const results = [];

  for (const script of document.scripts) {
    const src = script.src || '';
    if (!/cdn\.shopify\.com\/extensions\/.*wishlist-hero.*\/(default|bundle2)\.js/i.test(src)) {
      continue;
    }

    try {
      const text = await fetch(src).then((response) => response.text());
      const customEvents = [
        ...new Set([...text.matchAll(/wishlist-hero-add-to-custom[\w-]*/g)].map((m) => m[0])),
      ];
      const listeners = [
        ...new Set(
          [...text.matchAll(/addEventListener\(\s*["']([^"']+)["']/g)]
            .map((m) => m[1])
            .filter((name) => /wish|wlh|custom|hero|modal|hash/i.test(name))
        ),
      ].slice(0, 40);
      const openHints = [
        ...new Set(
          [...text.matchAll(/OpenWishList|openWishlist|hero-wishlist|location\.hash/g)].map((m) => m[0])
        ),
      ];

      const entry = { src, customEvents, listeners, openHints, bytes: text.length };
      results.push(entry);
      console.log('[WLH inspect]', entry);
    } catch (error) {
      console.warn('[WLH inspect] no se pudo leer', src, error);
    }
  }

  return results;
}

function exposeDebugApi() {
  window.__mulayaWlhDebug = printDiagnosis;
  window.__mulayaWlhOpen = openWishlistModal;
  window.__mulayaWlhAddFirst = async () => {
    const nativeBtn = document.querySelector(
      '.product-wishlist-native > button, #wishlisthero-product-page-button-container button'
    );
    if (nativeBtn instanceof HTMLElement) {
      nativeBtn.click();
      printDiagnosis();
      return true;
    }

    const host = document.querySelector('.product-wishlist-button, .wishlist-hero-product-control');
    if (!(host instanceof HTMLElement)) {
      console.warn('[WLH] No hay host de producto en esta página');
      return false;
    }
    await addFromProductControl(host);
    printDiagnosis();
    return true;
  };
  window.__mulayaWlhReport = diagnoseWishlistHero;
  window.__mulayaWlhInspectScripts = inspectWlhScripts;
  window.__mulayaWlhReinit = () => {
    whenWlhReady(() => {
      initCustomButtonsIn(document);
      printDiagnosis();
    });
  };
}

function boot() {
  ensureBridge();
  exposeDebugApi();

  const stored = readStoredCount();
  if (typeof stored === 'number') {
    paintHeaderCount(stored);
  }

  document.addEventListener('click', handleFavoritesClick, true);
  document.addEventListener('click', handleProductControlClick, true);
  document.addEventListener('click', handleNativePdpWishlistClick, true);
  document.addEventListener('mulaya:wishlist-updated', handleWishlistUpdated);
  window.addEventListener('hashchange', handleWishlistHashChange);
  document.addEventListener('wishlist-hero-wishlist-sdk-ready', () => {
    debugLog('evento wishlist-hero-wishlist-sdk-ready');
    onSdkReady();
    if (isDebugEnabled()) printDiagnosis();
  });

  // No lanzar el init “en serio” hasta que WLH deje de cargar.
  whenWlhReady(() => {
    placeNativePdpButton();
    if (window.WishListHero_SDK) {
      onSdkReady();
    } else {
      initCustomButtonsIn(document);
      syncAllHostAddedStates();
    }
    if (isDebugEnabled()) printDiagnosis();
  });

  scheduleInitRetries();
  waitForWlhScriptsThenInit();
  observeDom();

  // Eventos ready alternativos que a veces emite el App Embed
  for (const eventName of [
    'wishlist-hero-ready',
    'wishlisthero-ready',
    'wlh-ready',
    'wishlist-hero-loaded',
  ]) {
    document.addEventListener(eventName, () => {
      debugLog(`evento ${eventName}`);
      initCustomButtonsIn(document);
      if (isDebugEnabled()) printDiagnosis();
    });
    window.addEventListener(eventName, () => {
      debugLog(`window ${eventName}`);
      initCustomButtonsIn(document);
    });
  }

  if (isDebugEnabled()) {
    console.info('[WLH] debug ON (?wlh_debug=1 o localStorage mulaya:wlh_debug=1)');
    console.info('[WLH] Esperando __WishlistHero_Loading === false …');
    window.setTimeout(printDiagnosis, 800);
    window.setTimeout(printDiagnosis, 4000);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
